import { useApolloClient, useMutation } from "@apollo/client";
import { useCallback } from "react";
import { useElectronic } from "../context/ElectronicContext";
import { TAX_CODES } from "../Enums/ElectronicBill";
import SELL_CONDITION from "../Enums/SellCondition";
import {
  CREATE_EB_FROM_BILL,
  GET_EB_BY_ID,
  CREATE_MANY_ELECTRONIC_BILLS,
  ELECTRONIC_BILLS_EXIST,
  GET_EB_FOR_REVIEW,
  GET_BILL_BY_ID,
  SAVE_TEMP_BILL,
} from "../graphql";
import { client_EB_api } from "../graphql/client";
import {
  billConfigurationEmptyState,
  defaultTariff,
  defaultTax,
} from "../InitialStates/ElectronicContext";
import { formatElectronicBillDetailsFormattedWithMaxQuantity } from "../utils/electronicBill";
import getBill from "../utils/formatBill";
import { removeTypeName } from "../utils/helpers";
import {
  getSubTotal,
  getTaxedAmount,
  getTotal,
  getValuesSummatory,
} from "../utils/Operations";
import { selectProperBillingProfileFromPlace } from "../utils/placeUtils";

const useElectronicBill = () => {
  const [createTempBill] = useMutation(SAVE_TEMP_BILL);
  const client = useApolloClient();

  const {
    lineDetail,
    LineDetailPlace,
    receiver,
    electronicBill,
    setElectronicBill,
    setLineDetail,
    setLineDetailPlace,
    setReceiver,
    useCoinDetail,
    scheduledBill,
    setScheduledBill,
  } = useElectronic();

  const {
    handleCoinChange,
    getCoinsData,
    toDolar,
    toLocalExchangeRate,
    toExchangeRate,
  } = useCoinDetail;

  const getElectronicBill = async id => {
    const {
      data: { EBill },
    } = await client.query({
      query: GET_EB_BY_ID,
      variables: {
        id,
      },
      fetchPolicy: "no-cache",
    });
    return EBill;
  };
  const getElectronicBillForReview = useCallback(
    async id => {
      try {
        const {
          data: { bill },
        } = await client.query({
          query: GET_EB_FOR_REVIEW,
          variables: {
            id, // Add the FK_ElectronicBillDocumentType variable
          },
          fetchPolicy: "no-cache",
        });
        if (bill?.error) {
          return {
            error: true,
            message: bill.error,
          };
        }
        return bill;
      } catch (error) {
        throw new Error("Failed to fetch electronic bill for review");
      }
    },
    [client],
  );

  const getDraftBill = async id => {
    const {
      data: { bill },
    } = await client.query({
      query: GET_BILL_BY_ID,
      variables: {
        id,
      },
    });
    return bill;
  };

  const getBillByElectronicBill = _electronicBill =>
    _electronicBill?.Bill ??
    _electronicBill?.ReferencedDocument?.Bill ??
    _electronicBill?.ReferencedDocument?.ReferencedDocument?.Bill;

  const getLineDetailsAsDraftDetails = lineDetails =>
    lineDetail.map(detail => {
      const subTotal =
        getSubTotal(
          detail.unit_price,
          getValuesSummatory(detail.discount) / 100,
        ) * detail.quantity;
      const taxToUse = detail.billDetailTax ?? detail?.tax;
      const tariffToUse = detail.billDetailTariff ?? detail.Tariff;

      const taxedAmount = getTaxedAmount({
        taxCode: taxToUse?.MH_Code ?? TAX_CODES.IVA,
        subTotal,
        porcentajeTarifa: tariffToUse?.Percent,
        baseTax: 0,
        factor: 0,
      });

      const exemptAmount = subTotal - taxedAmount;
      const totalDiscountPercentages = getValuesSummatory(
        detail?.discountAmount || [0],
      );

      return {
        unitPrice: detail.unit_price,
        subTotal,
        totalPrice: getTotal(subTotal, taxedAmount),
        discount: parseFloat(totalDiscountPercentages),
        baseTax: toDolar(detail?.BaseTax) || 0,
        factor: detail?.Factor || 0,
        exempt: exemptAmount,
        taxed: taxedAmount,
        tariff: removeTypeName(tariffToUse ?? defaultTariff),
        tax: removeTypeName(taxToUse ?? defaultTax),
        MeasurementUnit: detail?.unit_Measure?.id,
        FK_ProductFeature: parseInt(detail.id, 10),
        quantity: detail.quantity,
        Total_BillDetail: toDolar(getTotal(subTotal, taxedAmount)),
        FK_Cabys: Number(detail.cabys.id),
        FK_Tax: taxToUse?.id,
        FK_Tariff: tariffToUse?.id,
      };
    });

  const saveTempralBill = async () => {
    const Coin = removeTypeName(
      await getCoinsData.coins.find(
        element => element.id === useCoinDetail.currentCoin,
      ),
    );
    const lineDetailNew = getLineDetailsAsDraftDetails();
    const {
      data: { bill },
    } = await createTempBill({
      variables: {
        bill_new: {
          FK_Place: Number(LineDetailPlace.id),
          FK_User: receiver.user.id,
          FK_SellCondition: electronicBill.Sell_Condition,
          FK_PaymentMethod: electronicBill.Payment_Method,
          FK_BillingProfile:
            selectProperBillingProfileFromPlace(LineDetailPlace)?.id,
          FK_activity_emitter:
            electronicBill?.activity_emitter?.value ||
            LineDetailPlace?.Activity?.id_MH,
          FK_activity_receiver: electronicBill?.activity_receiver?.value,
          Coin,
          Payment_Detail: electronicBill.OtherPaymentMethod,
          SellCondition_Detail:
            electronicBill.Sell_Condition === SELL_CONDITION.CREDITO.ID
              ? electronicBill.CreditPaymentCondition
              : electronicBill.OtherSellCondition,
          FK_BillType: electronicBill.BillType,
        },
        detail_new: lineDetailNew,
        detail: lineDetail.map(detail => ({
          FK_ProductFeature: parseInt(detail.value, 10),
          Quantity_BillDetail: detail.quantity,
          Total_BillDetail: detail.total,
          SubTotal_BillDetail: detail.unit_price,
          BaseTax: toDolar(parseFloat(detail?.BaseTax).toFixed(4)) || 0,
          Factor: parseFloat(detail?.Factor || 0).toFixed(4),
        })),
        config_new: {
          date: `${scheduledBill.Year}-${scheduledBill.Month}-${scheduledBill.Day} 00:00:00`,
          Day: scheduledBill.Day,
          Month: scheduledBill.Month,
          Year: scheduledBill.Year,
          Minute: scheduledBill.Minute,
          Hour: scheduledBill.Hours,
          Frequency: scheduledBill.Frequency,
          Active: scheduledBill.Active,
          Next_Execution: `${scheduledBill.Year}-${scheduledBill.Month}-${scheduledBill.Day} ${scheduledBill.Hours}:${scheduledBill.Minute}:00`,
        },
      },
    });
    return bill;
  };
  const saveEBillFormBill = async id => {
    try {
      const {
        data: { bill },
      } = await client_EB_api.mutate({
        mutation: CREATE_EB_FROM_BILL,
        variables: {
          id: Number(id),
        },
      });
      if (bill?.error) {
        return {
          error: true,
          message: bill.error,
        };
      }
      return bill;
    } catch (error) {
      throw new Error("Failed to save electronic bill from bill");
    }
  };

  const saveElectronicBillsXML = async (bills, OwnerPlaceId) => {
    const { data } = await client.mutate({
      mutation: CREATE_MANY_ELECTRONIC_BILLS,
      variables: {
        electronicBills: bills,
        OwnerPlace: OwnerPlaceId,
      },
    });
    const { createManyElectronicBills } = data;
    return createManyElectronicBills;
  };

  const validateIfElectronicBillExist = async newBills => {
    const { data } = await client.query({
      query: ELECTRONIC_BILLS_EXIST,
      variables: {
        electronicBills: newBills,
      },
    });
    const { electronicBillExist } = data;
    return electronicBillExist;
  };

  const handleElectronicBillsXML = async (
    tavuelUser,
    electronicBills,
    OwnerPlaceId,
    flowType,
  ) => {
    const { id } = tavuelUser;
    const platformExchnage = toLocalExchangeRate(1, getCoinsData?.coins[0]);
    const selectedElectronicBills = electronicBills
      .filter(invoice => invoice.selected)
      .map(invoice =>
        getBill(invoice, flowType, id, getCoinsData.coins, platformExchnage),
      );

    const response = {
      response: null,
      message: [],
      success: null,
    };
    if (selectedElectronicBills.length === 0) {
      return {
        response: "error",
        message: "No se ha seleccionado alguna factura",
      };
    }

    const responseBill = await saveElectronicBillsXML(
      selectedElectronicBills,
      OwnerPlaceId,
    );

    if (responseBill) {
      const { billDenid, billAccepted } = responseBill.reduce(
        (acc, bill) => {
          if (bill.error) {
            acc.billDenid.push(bill);
            return acc;
          }
          acc.billAccepted.push(bill);
          return acc;
        },
        { billDenid: [], billAccepted: [] },
      );
      if (billDenid.length > 0) {
        response.response = "errors";
        response.message.push(...billDenid.map(({ key }) => key));
      }
      if (billAccepted.length > 0) {
        response.success = "Facturas guardadas con éxito";
      }
      return response;
    }

    return { response: "error", message: "Ha ocurrido un error" };
  };

  const loadElectronicBill = async (id, isNote = false) => {
    const data = await getElectronicBill(id);
    if (!data) return;

    const formattedElectronicBill =
      formatElectronicBillDetailsFormattedWithMaxQuantity(data);

    const products = formattedElectronicBill?.ElectronicBillDetail?.map(
      index => {
        const feature = formattedElectronicBill?.Bill?.Detail?.find(
          detail => detail?.ProductFeature?.id === index?.ProductFeature?.id,
        );
        if (isNote || feature)
          return {
            ...index,
            Quantity_BillDetail:
              isNote || !feature
                ? index.Quantity
                : feature?.Quantity_BillDetail,
            Total_BillDetail:
              isNote || !feature
                ? index.Price * index.Quantity
                : feature?.Total_BillDetail,
          };
        return null;
      },
    ).filter(index => index?.id);

    const result = Array.isArray(products)
      ? products.map((item, index) => {
          const {
            ProductFeature: {
              Details,
              Product,
              ProductPrice,
              Files,
              id: productFeature_id,
            },
          } = item;

          return {
            detail_id: item.id,
            id: productFeature_id,
            description: Product?.Description_Product,
            cabys: ProductPrice?.Cabys,
            billDetailTariff:
              formattedElectronicBill.Bill?.Detail?.[index]?.Tariff,
            Tariff: ProductPrice?.Tariff,
            unit_price: ProductPrice?.Price_ProductPrice,
            quantity: item?.Quantity_BillDetail,
            discountAmount: [toExchangeRate(item?.discount_BillDetail)],
            tax: ProductPrice?.Tax,
            taxes: [],
            totalTaxes: 0,
            total: item?.Total_BillDetail,
            unit_Measure:
              item.ProductFeature.InventoryDetail?.UnitOfMeasurement,
            product_Code: "",
            url: Files,
            details: Details,
            value: item.id,
            label: Product?.Description_Product,
            checked: true,
            valid: true,
            maxQuantity: item.maxQuantity,
          };
        })
      : [];
    const place = data?.Place;

    setLineDetailPlace({
      id: place?.id,
      Name_Place: place?.Name_Place,
      Company: { id: place?.Company.id },
      Code: place?.Code || "000",
      IsUsingDefaultBillingProfile: place.IsUsingDefaultBillingProfile,
      BillingProfile: {
        id: place?.BillingProfile?.id,
        FK_User: place?.BillingProfile?.FK_User,
        MH_User: {
          FileEB: { name: place?.BillingProfile?.MH_User?.FileEB.name },
        },
      },
      DefaultBillingProfile: {
        id: place?.DefaultBillingProfile?.id,
        MH_User: {
          FileEB: { name: place?.DefaultBillingProfile?.MH_User?.FileEB.name },
        },
      },
    });

    data?.Receiver?.UserBillingProfile &&
      setReceiver({
        id: data?.Receiver?.id,
        Receiver_Id: data?.Receiver?.UserBillingProfile.ID_Number,
        Receiver_Email: data?.Receiver?.UserBillingProfile.Email,
        Receiver_Name: data?.Receiver?.UserBillingProfile.Name,
        Receiver_PhoneNumber: data?.Receiver?.UserBillingProfile.PhoneNumber,
      });
    setLineDetail(result);
    setElectronicBill({
      id: data?.id,
      Sell_Condition: data?.SellCondition,
      Coin: data?.FK_Coin,
      Payment_Method: data?.PaymentMethod,
      OtherPaymentMethod: data?.Bill?.Payment_Detail,
      OtherSellCondition: data?.Bill?.SellCondition_Detail,
      CreditPaymentCondition: data?.Bill?.SellCondition_Detail,
      FK_ElectronicBillDocumentType: data.ElectronicBillDocumentType.id,
      FK_ReferencedElectronicBill: data.ReferencedDocument?.id,
      ReceiverActivity: data?.Bill?.ReceiverActivity,
      EmitterActivity: data?.Bill?.EmitterActivity,
    });
    handleCoinChange(data?.Coin?.id);
  };

  const loadDraftBill = async id => {
    const data = await getDraftBill(id);
    if (!data) return;

    const products = data?.Detail;
    const result = Array.isArray(products)
      ? products.map(item => {
          const {
            ProductFeature: {
              Details,
              Product,
              ProductPrice,
              Files,
              id: productFeature_id,
            },
          } = item;

          return {
            detail_id: item.id,
            id: productFeature_id,
            description: Product?.Description_Product,
            cabys: ProductPrice?.Cabys,
            Tariff: ProductPrice?.Tariff,
            unit_price: ProductPrice?.Price_ProductPrice,
            quantity: item?.Quantity_BillDetail,
            BaseTax: item?.BaseTax,
            Factor: item?.Factor,
            discountAmount: [
              toExchangeRate(item?.discount_BillDetail.toFixed(3)),
            ],
            tax: ProductPrice?.Tax,
            taxes: [],
            totalTaxes: 0,
            total: item.Total_BillDetail.toFixed(3),
            Taxed: item.Taxed,
            unit_Measure: "",
            product_Code: "",
            url: Files,
            details: Details,
            value: item.id,
            label: Product?.Description_Product,
          };
        })
      : [];
    const place =
      data.Detail[0].ProductFeature.InventoryDetail.Inventory.Cellar.Place;
    setLineDetailPlace({
      id: place?.id,
      Name_Place: place?.Name_Place,
      Company: { id: place?.Company.id },
      Code: place?.Code || "000",
      IsUsingDefaultBillingProfile: place.IsUsingDefaultBillingProfile,
      BillingProfile: {
        id: place?.BillingProfile?.id,
        FK_User: place?.BillingProfile?.FK_User,
        MH_User: {
          FileEB: { name: place?.BillingProfile?.MH_User?.FileEB.name },
        },
      },
      DefaultBillingProfile: {
        id: place?.DefaultBillingProfile?.id,
        MH_User: {
          FileEB: { name: place?.DefaultBillingProfile?.MH_User?.FileEB.name },
        },
      },
    });
    data?.User?.id &&
      setReceiver({
        id: data?.User?.id,
        Receiver_Id: data?.User?.UserBillingProfile.ID_Number,
        Receiver_Email: data?.User?.UserBillingProfile.Email,
        Receiver_Name: data?.User?.UserBillingProfile.Name,
        Receiver_PhoneNumber: data?.User?.UserBillingProfile.PhoneNumber,
      });
    setLineDetail(result);
    setElectronicBill({
      id: data?.id,
      Sell_Condition: data?.FK_SellCondition,
      Coin: data?.FK_Coin,
      Payment_Method: data?.FK_PaymentMethod,
      OtherPaymentMethod: data?.Payment_Detail,
      OtherSellCondition: data?.SellCondition_Detail,
      CreditPaymentCondition: data?.SellCondition_Detail,
    });
    handleCoinChange(data?.FK_Coin);
    if (data.BillConfiguration !== null) {
      const infoDate = new Date(data?.BillConfiguration?.date);
      setScheduledBill({
        date: infoDate,
        Day: infoDate.getDate(),
        Month: infoDate.getMonth(),
        Year: infoDate.getFullYear(),
        Hour: `${data?.BillConfiguration?.Hour}${":"}${data?.BillConfiguration?.Minute}`,
        Minute: data.BillConfiguration.Minute,
        Hours: data.BillConfiguration.Hour,
        Frequency: data?.BillConfiguration?.Frequency,
        Active: data?.BillConfiguration?.Active,
      });
    } else {
      setScheduledBill(billConfigurationEmptyState);
    }
  };

  const formatBillDetailToTable = (billDetail, symbolCoin) => {
    const product = billDetail?.ProductFeature?.Product;
    const name = product?.Name_Product?.trim();
    const description = product?.Description_Product?.trim();

    const subtotal = billDetail?.SubTotal_BillDetail;
    const discountedAmount = billDetail?.discount_BillDetail || 0;
    const totalLineDiscountPercentage =
      (discountedAmount / (subtotal + discountedAmount)) * 100;
    const taxPercent =
      billDetail?.Tariff?.Percent ??
      billDetail?.ProductFeature?.ProductPrice?.Tariff?.Percent ??
      0;
    return {
      Line: billDetail.index,
      UnitPrice:
        symbolCoin + toExchangeRate(billDetail.UnitPrice || 0).toFixed(2),
      Quantity: billDetail?.Quantity,
      Name: name || "---",
      MeasurementUnit:
        billDetail?.UnitOfMeasurement?.Name_UnitOfMeasurement || "---",
      Detail: description || "---",
      Iva: `${taxPercent} %`,
      Subtotal: symbolCoin + toExchangeRate(subtotal).toFixed(2),
      Amount:
        symbolCoin + toExchangeRate(billDetail?.Total_BillDetail).toFixed(2),
      Discount: `${totalLineDiscountPercentage || 0} %`,
      productFeatureId: billDetail?.ProductFeature?.id || 0,
    };
  };
  const getBillDetailsByElectronicBill = (_electronicBill, isDebitNote) =>
    _electronicBill?.Bill?.Detail ??
    _electronicBill?.ReferencedDocument?.Bill?.Detail ??
    _electronicBill?.ReferencedDocument?.ReferencedDocument?.Bill?.Detail ??
    [];

  const formatProductsWithCorrectPriceAndQuantity = (
    electronicBillDetails,
    billDetails,
  ) =>
    electronicBillDetails.map(electronicBillDetail => {
      const foundBillItem = billDetails.find(
        billDetail =>
          billDetail?.ProductFeature?.id ===
          electronicBillDetail?.ProductFeature?.id,
      );

      return {
        ...foundBillItem,
        Quantity_BillDetail: electronicBillDetail?.Quantity ?? 0,
      };
    });

  return {
    saveTempralBill,
    saveEBillFormBill,
    getElectronicBill,
    loadElectronicBill,
    saveElectronicBillsXML,
    validateIfElectronicBillExist,
    handleElectronicBillsXML,
    getElectronicBillForReview,
    LineDetailPlace,
    getDraftBill,
    loadDraftBill,
    formatBillDetailToTable,
    getBillDetailsByElectronicBill,
    formatProductsWithCorrectPriceAndQuantity,
    getBillByElectronicBill,
  };
};

export default useElectronicBill;
